import classnames from 'classnames';
import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'store';
import { ThemeContext } from 'styled-components';

import AlertBox from 'components/AlertBox';
import Button from 'components/Button';
import DatePickerGroup from 'components/DatePickerGroup';
import DestinationSearchInput from 'components/DestinationSearchInput';
import Icon from 'components/Icon';
import OccupancyInput from 'components/OccupancyInput';

import { useStartSearchMutation } from 'store/queries/search';
import { selectBrowser } from 'store/slices/browser';
import {
    selectArrival,
    selectCountry,
    selectDeparture,
    selectDestination,
    selectProperty,
    selectResort,
    selectRooms,
    selectTerm,
} from 'store/slices/searchBox';

const SearchBox = ({ className, floating, type = 'vertical' }) => {
    const history = useHistory();
    const [
        startSearch,
        { data, isLoading, isSuccess, error, isError },
    ] = useStartSearchMutation();
    const themeContext = useContext(ThemeContext);
    const arrival = useSelector(selectArrival);
    const departure = useSelector(selectDeparture);
    const term = useSelector(selectTerm);
    const property = useSelector(selectProperty);
    const resort = useSelector(selectResort);
    const destination = useSelector(selectDestination);
    const country = useSelector(selectCountry);
    const rooms = useSelector(selectRooms);
    const browser = useSelector(selectBrowser);

    const start = (e) => {
        e.stopPropagation();
        e.preventDefault();

        startSearch({
            countryId: country.id,
            destinationId: destination.id,
            resortId: resort?.id ?? '',
            propertyId: property?.id ?? '',
            currency: 'GBP',
            term,
            arrival: arrival.format('DD/MM/YYYY'),
            departure: departure.format('DD/MM/YYYY'),
            rooms: rooms.length,
            occupancy: rooms.map((room) => ({
                adults: room.adults.length,
                children: room.children.length,
                ages: room.children.map((child) => child.age),
            })),
        });
    };

    useEffect(() => {
        if (isSuccess && data.sid) {
            history.push(`/search/results.html?sid=${data.sid}`);
        }
    }, [isSuccess, data, history]);

    return (
        <div className={classnames(className, type, { floating })}>
            <div className="form">
                {isError ? (
                    <div className="errors-container">
                        <AlertBox>
                            <strong>
                                Please check your entries and try again:
                            </strong>
                            <ul className="error-list">
                                {Object.keys(error.data).map((key) => (
                                    <li key={key}>{error.data[key]}</li>
                                ))}
                            </ul>
                        </AlertBox>
                    </div>
                ) : null}
                <div className="destination-input-container">
                    <div className="destination-input">
                        <DestinationSearchInput
                            key={term}
                            backgroundColor={
                                floating ? themeContext.colours.lightGrey : null
                            }
                            term={term}
                            browser={browser}
                        />
                    </div>
                </div>
                <div className="dates-container">
                    <DatePickerGroup
                        backgroundColor={
                            floating ? themeContext.colours.lightGrey : null
                        }
                    />
                </div>
                <div className="occupancy-container">
                    <OccupancyInput
                        backgroundColor={
                            floating ? themeContext.colours.lightGrey : null
                        }
                        fullWidth={floating || browser.size === 'xs'}
                    />
                </div>
                <div className="submit-container">
                    <span onClick={isLoading ? null : start}>
                        <Button
                            fullWidth
                            btnStyle={
                                type === 'vertical' ? 'primary' : 'default'
                            }
                            thick
                            fake
                        >
                            {isLoading ? (
                                <Icon type="loading" color="#ffffff" />
                            ) : type === 'vertical' ? (
                                'Find hotel deals'
                            ) : (
                                'Search'
                            )}
                        </Button>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default SearchBox;
